import { createRoot } from 'react-dom/client';
import { Provider } from "react-redux";
import { ConfirmProvider } from 'material-ui-confirm';
import "@common-config/i18next.config";
import reportWebVitals from "@common/site/reportWebVitals";
import { getAssetURL } from '@common-components/theme/theme-asset';
import WebsiteComponent from "@website/website";
import store from "@website/store/web-store";
import { initializeAttribution } from '@common/utils/attribution';
import { captureReferralFromUrl } from '@common/utils/referral-code';
import { initAuth } from "@common/bt-auth/AuthThunks";
import { logout } from "@common-reducers/UserReducer";
import { BLUETICKS_TOKEN_KEY } from "@common/reusable/common";
import '../styles/globals.css';
import '../styles/globals-website.css';
import '../styles/rtl.css';

// Auth fan-in: the extension broadcasts login/logout to all surfaces; the co-injected
// content script (app.js / attribution-bridge) relays them to this page world as
// `bt-extension-login` / `bt-extension-logout`, so the web app mirrors the extension's
// session at runtime (e.g. right after logging in/out via the popup). Both are gated by
// `isSyncLoginLogoutEnable` at the content-script bridge, so when sync is off the page never
// receives them. Origin/source gated like the auth-bridge security gates.
if (typeof window !== "undefined") {
  // The extension re-broadcasts the same login several times per page load. Dedup on the
  // token so we only persist + run initAuth once per distinct token: a repeat of the SAME
  // token is a no-op (initAuth/attemptJwtAuth would skip-guard it anyway), while a NEW token
  // (e.g. switching accounts) still flows through. Without this we fired initAuth on every
  // broadcast — wasteful, and it previously re-triggered the auth-status churn.
  let lastBtLoginToken: string | undefined;
  window.addEventListener("message", (event: MessageEvent) => {
    if (event.source !== window || event.origin !== window.location.origin) return;
    const data = event?.data;

    if (data?.cmd === "bt-extension-login") {
      const accessToken: string | undefined = data?.data?.accessToken;
      if (!accessToken) return;
      if (accessToken === lastBtLoginToken) return; // same token already processed this session
      lastBtLoginToken = accessToken;
      console.log("[login-flow] [website] bt-extension-login received -> logging in");
      try { localStorage.setItem(BLUETICKS_TOKEN_KEY, accessToken); } catch (e) { /* ignore */ }
      store.dispatch(initAuth());
      return;
    }

    if (data?.cmd === "bt-extension-logout") {
      // Counterpart to bt-extension-login: an extension logout must log the web app out too.
      // Reset the login dedup so a later re-login (even with the same token) is processed.
      lastBtLoginToken = undefined;
      console.log("[login-flow] [website] bt-extension-logout received -> logging out");
      try { localStorage.removeItem(BLUETICKS_TOKEN_KEY); } catch (e) { /* ignore */ }
      store.dispatch(logout());
      return;
    }
  });
}

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error('[website] #root element not found');
}
const root = createRoot(rootEl);

document.addEventListener("DOMContentLoaded", function (event) {
  const faviconURL = getAssetURL('favicon.ico'); // Assuming your favicon's name is 'favicon.ico'
  (window as any).__APP_STORE__ = store;

  let faviconLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement;;
  if (!faviconLink) {
    faviconLink = document.createElement('link') as HTMLLinkElement;;
    faviconLink.rel = 'icon';
    document.head.appendChild(faviconLink);
  }
  faviconLink.href = faviconURL;

  // Initialize attribution tracking on app load
  initializeAttribution();
  captureReferralFromUrl();

  root.render(
    <div className="btcss">
      <Provider store={store}>
        <ConfirmProvider>
          <WebsiteComponent />
        </ConfirmProvider>
      </Provider>
    </div>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
